import { useEffect, useState } from 'react'

import { captureException } from '@sentry/nextjs'

import { isValidFileType } from '@/features/device-crud'

import {
  CSVParseResultI,
  ParsedFacilityI,
  parseCSVFile,
} from '../utils/csvParser'
import { geocodeAddress, getTimezoneFromCoordinates } from '../utils/geocoding'

interface GeocodingProgressI {
  current: number
  total: number
}

interface UseFacilityCSVProcessorReturnI {
  isLoading: boolean
  isGeocoding: boolean
  geocodingProgress: GeocodingProgressI
}

const intialGeocodingProgressState: GeocodingProgressI = {
  current: 0,
  total: 0,
}

/**
 * Custom hook to handle CSV parsing, geocoding, and timezone lookup for bulk facility uploads
 *
 * @param fileUpload - The uploaded CSV file(s) from react-hook-form
 * @param onParseResultChange - Callback to update parse result in parent component
 * @returns Object containing loading states and geocoding progress
 *
 */
export const useFacilityCSVProcessor = (
  fileUpload: FileList | null,
  onParseResultChange: (result: CSVParseResultI | null) => void
): UseFacilityCSVProcessorReturnI => {
  const [isLoading, setIsLoading] = useState(false)
  const [isGeocoding, setIsGeocoding] = useState(false)
  const [geocodingProgress, setGeocodingProgress] =
    useState<GeocodingProgressI>(intialGeocodingProgressState)

  useEffect(() => {
    const parseAndGeocodeFile = async () => {
      // Early return: Reset state if no file
      if (!fileUpload || fileUpload.length === 0) {
        onParseResultChange(null)
        setGeocodingProgress(intialGeocodingProgressState)
        return
      }

      // Early return: Validate file type
      if (!isValidFileType(fileUpload)) {
        return
      }

      setIsLoading(true)
      try {
        // Step 1: Parse the CSV file
        const result = await parseCSVFile(fileUpload[0])

        // Early return: Handle empty CSV
        if (result.data.length === 0) {
          onParseResultChange(result)
          return
        }

        // Step 2: Determine processing needed
        const facilitiesNeedingGeocode = result.data.filter(
          (facility) => !doCoordsHaveContent(facility)
        )
        const facilitiesNeedingTimezone = result.data.filter(needsTimezoneOnly)

        // Case 1: No processing needed at all
        if (
          facilitiesNeedingGeocode.length === 0 &&
          facilitiesNeedingTimezone.length === 0
        ) {
          onParseResultChange({
            ...result,
            data: removeUploadStatusFields(result.data),
          })
          return
        }

        // Case 2: Only timezone lookup needed (no geocoding)
        if (facilitiesNeedingGeocode.length === 0) {
          const facilitiesWithTimezone = fillMissingTimezones(result.data)
          onParseResultChange({
            ...result,
            data: removeUploadStatusFields(facilitiesWithTimezone),
          })
          return
        }

        // Case 3: Geocoding needed - transition from loading to geocoding state
        setIsLoading(false)
        setIsGeocoding(true)
        setGeocodingProgress({
          current: 0,
          total: facilitiesNeedingGeocode.length,
        })

        // Perform geocoding with progress tracking
        const geocodedFacilities = await geocodeFacilities(
          result.data,
          (current) => {
            setGeocodingProgress((prev) => ({ ...prev, current }))
          }
        )

        // Fill in missing timezones for all facilities with coordinates
        const facilitiesWithTimezone = fillMissingTimezones(geocodedFacilities)

        // Build final result with geocoding errors
        const geocodingErrors = buildGeocodingErrors(facilitiesWithTimezone)
        const finalResult: CSVParseResultI = {
          ...result,
          data: removeUploadStatusFields(facilitiesWithTimezone),
          errors:
            geocodingErrors.length > 0
              ? [...result.errors, ...geocodingErrors]
              : result.errors,
        }

        onParseResultChange(finalResult)
      } catch (error) {
        onParseResultChange({
          data: [],
          errors: ['Failed to parse CSV file'],
        })
        captureException(error)
      } finally {
        setIsLoading(false)
        setIsGeocoding(false)
        setGeocodingProgress(intialGeocodingProgressState)
      }
    }

    parseAndGeocodeFile()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileUpload])

  return {
    isLoading,
    isGeocoding,
    geocodingProgress,
  }
}

/**
 * Checks if a facility has valid latitude and longitude coordinates
 * @param facility - The facility to check
 * @returns True if facility has non-empty lat and lon values
 */
const doCoordsHaveContent = (facility: ParsedFacilityI): boolean => {
  return !!(
    facility.lat &&
    facility.lon &&
    facility.lat.trim() !== '' &&
    facility.lon.trim() !== ''
  )
}

/**
 * Checks if a facility is missing timezone but has valid coordinates
 * @param facility - The facility to check
 * @returns True if timezone is missing but coordinates exist
 */
const needsTimezoneOnly = (facility: ParsedFacilityI): boolean => {
  return (
    (!facility.timezone || facility.timezone.trim() === '') &&
    doCoordsHaveContent(facility)
  )
}

/**
 * Removes upload status fields from facilities for clean display
 * @param facilities - Array of facilities
 * @returns Array of facilities without upload status fields
 */
const removeUploadStatusFields = (
  facilities: ParsedFacilityI[]
): ParsedFacilityI[] => {
  return facilities.map((facility) => ({
    ...facility,
    uploadStatus: undefined,
    uploadError: undefined,
  }))
}

/**
 * Builds error messages for geocoding failures
 * @param facilities - Array of facilities with potential geocoding errors
 * @returns Array of error message strings
 */
const buildGeocodingErrors = (facilities: ParsedFacilityI[]): string[] => {
  return facilities
    .filter((f) => f.geocodingError)
    .map((f, idx) => `Row ${idx + 2}: ${f.geocodingError} for "${f.address}"`)
}

/**
 * Updates a facility with successful geocoding results
 * @param facility - The facility to update
 * @param geocodingResult - The successful geocoding result
 * @returns Updated facility with coordinates and timezone
 */
const applyGeocodingSuccess = (
  facility: ParsedFacilityI,
  geocodingResult: { lat: string; lon: string; timezone?: string }
): ParsedFacilityI => ({
  ...facility,
  lat: geocodingResult.lat,
  lon: geocodingResult.lon,
  timezone: geocodingResult.timezone || facility.timezone,
  geocodingError: undefined,
})

/**
 * Updates a facility with geocoding failure
 * @param facility - The facility to update
 * @param error - The error message
 * @returns Updated facility with error
 */
const applyGeocodingFailure = (
  facility: ParsedFacilityI,
  error?: string
): ParsedFacilityI => ({
  ...facility,
  lat: '',
  lon: '',
  geocodingError: error || 'Failed to geocode address',
})

/**
 * Geocodes facilities with missing coordinates
 * @param facilities - Array of parsed facilities
 * @param onProgress - Callback for progress updates
 * @returns Promise resolving to array of facilities with geocoded coordinates
 */
const geocodeFacilities = async (
  facilities: ParsedFacilityI[],
  onProgress: (current: number) => void
): Promise<ParsedFacilityI[]> => {
  const geocodedFacilities: ParsedFacilityI[] = [...facilities]
  let processedCount = 0

  for (let i = 0; i < facilities.length; i++) {
    const facility = facilities[i]

    // Skip facilities that already have valid coordinates
    if (doCoordsHaveContent(facility)) {
      continue
    }

    // Attempt to geocode the facility address
    const geocodingResult = await geocodeAddress(facility.address)

    // Update progress
    processedCount++
    onProgress(processedCount)

    // Apply geocoding result to facility
    geocodedFacilities[i] = geocodingResult.success
      ? applyGeocodingSuccess(facility, geocodingResult)
      : applyGeocodingFailure(facility, geocodingResult.error)

    // Rate limiting delay to avoid API quota issues
    await new Promise((resolve) => setTimeout(resolve, 100))
  }

  return geocodedFacilities
}

/**
 * Fills in missing timezones for facilities that have coordinates
 * @param facilities - Array of parsed facilities
 * @returns Array of facilities with timezones filled in
 */
const fillMissingTimezones = (
  facilities: ParsedFacilityI[]
): ParsedFacilityI[] => {
  return facilities.map((facility) => {
    // If timezone is missing but coordinates exist, look up timezone
    if (
      (!facility.timezone || facility.timezone.trim() === '') &&
      doCoordsHaveContent(facility)
    ) {
      const timezone = getTimezoneFromCoordinates(facility.lat, facility.lon)
      if (timezone) {
        return {
          ...facility,
          timezone,
        }
      }
    }
    return facility
  })
}
